import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { mapboxAccessToken } from '~/utils/constants/env';
import { getErrorMessage } from '~/utils/helper/error';
import seniorApi, { ISeniorProfileResponse } from '~/apis/senior';
import {
  COGNITIVE_DECLINE_OPTIONS,
  IOption,
  NAVIGATION_LANGUAGE_OPTIONS,
  TEXT_LANGUAGE_OPTIONS,
  VOICE_LANGUAGE_OPTIONS,
  getTimezoneOptions,
} from '~/utils/constants/profileOptions';
import { canDoAction } from '~/utils/helper/permission';
import { ADDRESS_FIELDS, addressFromFormCompact, addressToForm, toMultipart } from '~/utils/helper/setupForm';
import { FormValues } from '~/utils/interface/setup';
import { useAppSelector } from '~/utils/redux/hooks';
import { selectPermission, selectUser } from '~/thunks/auth/authSlice';

// mapbox-gl is large; it loads only when the Profile page is opened with a Mapbox token.
const LocationMapField = lazy(() => import('~/components/module/setup/LocationMapField'));

const inputClass =
  'w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground disabled:opacity-60';

// Defaults match the app: US English, level 1, US Eastern.
const toForm = (data: ISeniorProfileResponse): FormValues => ({
  firstName: data.profile.firstName ?? '',
  lastName: data.profile.lastName ?? '',
  avatarImage: data.profile.avatarImage ?? '',
  ...addressToForm(data.profile.homeAddress),
  cognitiveDeclineLevel: String(data.cognitiveDeclineLevel ?? 1),
  timezone: data.timezone ?? 'America/New_York',
  languageCode: data.languageCode ?? 'en-US',
  languageVoiceCode: data.languageVoiceCode ?? 'en-US',
  navigationLanguageCode: data.navigationLanguageCode ?? 'en-US',
});

// Only these go in the PUT when they actually changed.
const SETTING_KEYS = ['cognitiveDeclineLevel', 'timezone', 'languageCode', 'languageVoiceCode', 'navigationLanguageCode'];

const str = (value: FormValues[string]) => (typeof value === 'string' || typeof value === 'number' ? String(value) : '');

const Profile = () => {
  const { t } = useTranslation();
  const user = useAppSelector(selectUser);
  const permission = useAppSelector(selectPermission);
  const canUpdate = canDoAction(user, permission, 'seniorProfile', 'editSeniorProfile');

  const [saved, setSaved] = useState<FormValues>();
  const [draft, setDraft] = useState<FormValues>();
  const [preview, setPreview] = useState<string>();
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [timezoneSearch, setTimezoneSearch] = useState('');
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string }>();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await seniorApi.getProfile();
      const next = toForm(resp.data.data);
      setSaved(next);
      setDraft(next);
    } catch {
      setFeedback({ type: 'error', message: t('pages.setup.loadFailed') });
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const file = draft?.avatarImage;
    if (!(file instanceof File)) return setPreview(undefined);
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [draft?.avatarImage]);

  const hasNewAvatar = draft?.avatarImage instanceof File;
  const hasFieldChanges =
    !!draft && !!saved && JSON.stringify({ ...draft, avatarImage: '' }) !== JSON.stringify({ ...saved, avatarImage: '' });
  const timezoneOptions = useMemo(() => getTimezoneOptions(str(saved?.timezone)), [saved?.timezone]);
  // the search narrows the list, but the selected zone always stays in it
  const visibleTimezones = useMemo(() => {
    const query = timezoneSearch.trim().toLowerCase().replace(/_/g, ' ');
    if (!query) return timezoneOptions;
    return timezoneOptions.filter(
      (option) => option.value === str(draft?.timezone) || option.label.toLowerCase().includes(query)
    );
  }, [timezoneOptions, timezoneSearch, draft?.timezone]);
  const isDirty = hasFieldChanges || hasNewAvatar;

  const patch = (partial: FormValues) => {
    setFeedback(undefined);
    setDraft((prev) => (prev ? { ...prev, ...partial } : prev));
  };

  const handleSync = async () => {
    if (!draft) return;
    setSyncing(true);
    setFeedback(undefined);
    try {
      await seniorApi.updateProfile(
        toMultipart({
          // the schema rejects '' for names, so a blank one is left out
          firstName: str(draft.firstName).trim() || undefined,
          lastName: str(draft.lastName).trim() || undefined,
          homeAddress: addressFromFormCompact(draft),
          avatarImage: draft.avatarImage instanceof File ? draft.avatarImage : undefined,
          ...Object.fromEntries(
            SETTING_KEYS.filter((key) => draft[key] !== saved?.[key]).map((key) => [
              key,
              key === 'cognitiveDeclineLevel' ? Number(draft[key]) : draft[key],
            ])
          ),
        })
      );
      await load();
      setFeedback({ type: 'success', message: t('pages.setup.syncSuccess') });
    } catch (error) {
      const reason = getErrorMessage(error);
      setFeedback({ type: 'error', message: reason ? `${t('pages.setup.syncFailed')} ${reason}` : t('pages.setup.syncFailed') });
    } finally {
      setSyncing(false);
    }
  };

  if (loading && !draft) return <p className='text-sm text-muted-foreground'>{t('common.loading')}</p>;
  if (!draft) return feedback ? <p className='text-sm text-destructive'>{feedback.message}</p> : null;

  const avatarSrc = preview ?? (typeof draft.avatarImage === 'string' && draft.avatarImage ? draft.avatarImage : undefined);
  const card = 'rounded-lg border border-border bg-card p-4 sm:p-5';
  const textField = (name: string, labelKey: string, type = 'text') => (
    <label key={name} className='flex flex-col gap-1 text-sm'>
      <span>{t(labelKey)}</span>
      <input
        type={type}
        className={inputClass}
        value={str(draft[name])}
        disabled={!canUpdate}
        onChange={(e) => patch({ [name]: e.target.value })}
      />
    </label>
  );

  const selectField = (name: string, labelKey: string, options: IOption[]) => (
    <label key={name} className='flex flex-col gap-1 text-sm'>
      <span>{t(labelKey)}</span>
      <select
        className={inputClass}
        value={str(draft[name])}
        disabled={!canUpdate}
        onChange={(e) => patch({ [name]: e.target.value })}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {t(option.labelKey)}
          </option>
        ))}
      </select>
    </label>
  );

  return (
    <div className='flex w-full flex-col gap-4'>
      <div className='flex flex-wrap items-center justify-between gap-3'>
        <div>
          <h1 className='text-xl font-semibold text-foreground'>{t('pages.setup.profile.title')}</h1>
          <p className='text-sm text-muted-foreground'>{t('pages.setup.profile.subtitle')}</p>
        </div>
        {canUpdate && (
          <button
            type='button'
            onClick={handleSync}
            disabled={!isDirty || syncing}
            className='rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50'
          >
            {syncing ? t('pages.setup.syncing') : t('pages.setup.sync')}
          </button>
        )}
      </div>

      {/* the header spans the page; the content below stays a readable width */}
      <div className='mx-auto flex w-full max-w-3xl flex-col gap-4'>
        {feedback && (
          <p className={`text-sm ${feedback.type === 'success' ? 'text-foreground' : 'text-destructive'}`} role='status'>
            {feedback.message}
          </p>
        )}
        {canUpdate && isDirty && <p className='text-xs text-muted-foreground'>{t('pages.setup.unsynced')}</p>}
        {!canUpdate && <p className='text-xs text-muted-foreground'>{t('pages.setup.readOnly')}</p>}

        <section className={`${card} flex flex-col gap-4`}>
          <div className='flex items-center gap-4'>
            <div className='h-16 w-16 shrink-0 overflow-hidden rounded-full bg-muted'>
              {avatarSrc && <img src={avatarSrc} alt='' className='h-full w-full object-cover' />}
            </div>
            {canUpdate && (
              <input
                type='file'
                accept='image/*'
                aria-label={t('pages.setup.fields.avatar')}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) patch({ avatarImage: file });
                }}
              />
            )}
          </div>
          {textField('firstName', 'pages.setup.fields.firstName')}
          {textField('lastName', 'pages.setup.fields.lastName')}
        </section>

        <section className={`${card} flex flex-col gap-4`}>
          <h2 className='text-sm font-semibold text-foreground'>{t('pages.setup.profile.homeAddress')}</h2>
          {mapboxAccessToken && canUpdate ? (
            // a typed address has no coordinates: the location is picked on the map, which fills lat/lng and the address
            <>
              <Suspense fallback={<p className='text-sm text-muted-foreground'>{t('common.loading')}</p>}>
                <LocationMapField values={draft} onChange={patch} showRadius={false} fillRegion />
              </Suspense>
              {textField('city', 'pages.setup.fields.city')}
              {textField('country', 'pages.setup.fields.country')}
            </>
          ) : (
            ADDRESS_FIELDS.map((field) => textField(field.name, field.labelKey, field.type))
          )}
        </section>

        <section className={`${card} flex flex-col gap-4`}>
          {selectField('cognitiveDeclineLevel', 'pages.setup.profile.cognitiveDeclineLevel', COGNITIVE_DECLINE_OPTIONS)}
          <div className='flex flex-col gap-1 text-sm'>
            <span>{t('pages.setup.profile.timezone')}</span>
            <span className='font-medium text-foreground'>
              {timezoneOptions.find((option) => option.value === str(draft.timezone))?.label ?? str(draft.timezone)}
            </span>
            {canUpdate && (
              <>
                <input
                  type='search'
                  className={inputClass}
                  value={timezoneSearch}
                  placeholder={t('pages.setup.profile.timezoneSearch')}
                  onChange={(e) => setTimezoneSearch(e.target.value)}
                />
                <ul
                  role='listbox'
                  aria-label={t('pages.setup.profile.timezone')}
                  className='max-h-56 overflow-y-auto rounded-md border border-border bg-background'
                >
                  {visibleTimezones.length === 0 && (
                    <li className='px-3 py-2 text-muted-foreground'>{t('pages.setup.profile.timezoneNoMatch')}</li>
                  )}
                  {visibleTimezones.map((option) => {
                    const selected = option.value === str(draft.timezone);
                    return (
                      <li key={option.value} role='option' aria-selected={selected}>
                        <button
                          type='button'
                          onClick={() => patch({ timezone: option.value })}
                          className={`w-full px-3 py-2 text-left hover:bg-muted ${
                            selected ? 'bg-accent font-semibold text-accent-foreground' : ''
                          }`}
                        >
                          {option.label}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
          </div>
          {selectField('languageCode', 'pages.setup.profile.languageText', TEXT_LANGUAGE_OPTIONS)}
          {selectField('languageVoiceCode', 'pages.setup.profile.languageVoice', VOICE_LANGUAGE_OPTIONS)}
          {selectField('navigationLanguageCode', 'pages.setup.profile.languageNavigation', NAVIGATION_LANGUAGE_OPTIONS)}
        </section>
      </div>
    </div>
  );
};

export default Profile;
